// Partial relay helper (network-relay-http-v1 "Browser boundary").
//
// After explicit sharing consent, one tab per origin keeps a small in-memory cache of verified pieces of OTHER
// demanded public files and advertises only those pieces. Rules this module enforces:
//  - one leader tab (Web Locks) owns every auxiliary byte and every pending fill until its cleanup has finished;
//    without reliable cross-tab coordination no auxiliary work happens at all;
//  - only leased indices are ever stored or advertised; a whole file is never allocated or `begin`-ed;
//  - peers first, the origin only after a bounded wait and only once per lease; every byte must match the leased
//    SHA-256 and be confirmed by the server before it is cached or advertised;
//  - opt-out, retargeting, a stale or refused authority, an epoch change, lease expiry, foreground pressure and page
//    destruction abort the work in flight and drop the auxiliary bytes. Prefill earns no useful credit.
import { digestHex } from '../sha256.js';

export const RELAY_LIMITS = Object.freeze({
  maxCacheBytes: 64 * 1024 * 1024,
  maxInventory: 16,
  maxRelease: 16,
  maxWant: 32,
  peerWaitMs: 4_000,
  minRefreshMs: 5_000,
  maxRefreshMs: 120_000,
  maxAuthorityMs: 60_000,
  requestTimeoutMs: 10_000,
  watchMs: 1_000,
  confirmAttempts: 6,
  lockName: 'solred-relay-v1',
});

const LEASE_KEYS = ['leaseId', 'resourceId', 'version', 'index', 'length', 'sha256', 'sha1', 'expiresAt'];
const LEASE_ID = /^l[0-9a-f]{32}$/;
const HEX64 = /^[0-9a-f]{64}$/;
const SAFE = /^[A-Za-z0-9_.:-]{1,200}$/;
const RELAY_PATH = /^\/hybrid-origin-v1\/objects\/o_[0-9a-f]{32}\/relay\/pieces\/(\d{1,9})$/;

/** A plan lease entry with exactly the contract keys (plus an optional relative metadata URL), or null. */
export function parseLease(raw, now = Date.now()) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const keys = Object.keys(raw).filter((k) => k !== 'metadataUrl');
  if (keys.length !== LEASE_KEYS.length || !LEASE_KEYS.every((k) => keys.includes(k))) return null;
  const { leaseId, resourceId, version, index, length, sha256, sha1, expiresAt } = raw;
  if (!LEASE_ID.test(leaseId) || !SAFE.test(resourceId) || !SAFE.test(version)) return null;
  if (!Number.isSafeInteger(index) || index < 0 || !Number.isSafeInteger(length) || length <= 0 || length > 16 * 1024 * 1024) return null;
  if (!HEX64.test(sha256) || (sha1 !== null && !/^[0-9a-f]{40}$/.test(sha1))) return null;
  if (!Number.isSafeInteger(expiresAt) || expiresAt <= now) return null;
  if (raw.metadataUrl !== undefined && (typeof raw.metadataUrl !== 'string' || !raw.metadataUrl.startsWith('/'))) return null;
  return { leaseId, resourceId, version, index, length, sha256, sha1, expiresAt, hasMetadata: raw.metadataUrl !== undefined };
}

/** The origin capability URL may only name this lease's exact auxiliary piece on an https (or loopback) node. */
export function checkOriginUrl(url, lease) {
  let u;
  try { u = new URL(String(url)); } catch { return null; }
  const loopback = u.protocol === 'http:' && /^(127\.0\.0\.1|localhost)$/.test(u.hostname);
  if ((u.protocol !== 'https:' && !loopback) || u.username || u.password || u.hash) return null;
  const m = RELAY_PATH.exec(u.pathname);
  if (!m || Number(m[1]) !== lease.index) return null;
  if ([...u.searchParams.keys()].join(',') !== 'cap') return null;
  return u.toString();
}

/** Reads exactly `length` bytes or returns null; never buffers beyond the lease. */
async function readExact(res, length, signal) {
  if (!res.body || typeof res.body.getReader !== 'function') {
    const declared = Number(res.headers?.get?.('content-length'));
    if (Number.isFinite(declared) && declared !== length) return null;
    const buf = new Uint8Array(await res.arrayBuffer());
    return buf.length === length ? buf : null;
  }
  const reader = res.body.getReader();
  const out = new Uint8Array(length);
  let at = 0;
  try {
    for (;;) {
      if (signal?.aborted) return null;
      const { done, value } = await reader.read();
      if (done) break;
      if (at + value.length > length) return null; // more than the lease: refused, never truncated
      out.set(value, at); at += value.length;
    }
  } finally { try { await reader.cancel(); } catch { /* already closed */ } }
  return at === length ? out : null;
}

/** A child signal aborted by the parent, by `ms` or explicitly. */
function childSignal(parent, ms) {
  const ctrl = new AbortController();
  const stop = () => ctrl.abort();
  if (parent.aborted) ctrl.abort(); else parent.addEventListener('abort', stop, { once: true });
  const timer = Number.isFinite(ms) ? setTimeout(stop, Math.max(0, ms)) : 0;
  return { ctrl, signal: ctrl.signal, done: () => { clearTimeout(timer); parent.removeEventListener('abort', stop); } };
}

function abortableWait(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const done = () => { clearTimeout(t); signal?.removeEventListener('abort', done); resolve(); };
    const t = setTimeout(done, Math.max(0, ms));
    signal?.addEventListener('abort', done, { once: true });
  });
}

/**
 * @param {object} o
 * @param {object} o.api  createApi() client with the relay methods (each accepts a trailing AbortSignal)
 * @param {LockManager|null} [o.locks]  Web Locks; absent => the helper stays disabled
 * @param {(url: string, init: RequestInit) => Promise<Response>} [o.fetch]
 * @param {(p: object) => Promise<object|null>} [o.joinPartial]  peers adapter (peers/webtorrent.js); absent => origin only
 * @param {() => number} [o.now]
 * @param {(ms: number, signal: AbortSignal) => Promise<void>} [o.sleep]  refresh wait
 * @param {(ms: number, signal: AbortSignal) => Promise<void>} [o.delay]  confirmation backoff
 * @param {number} [o.watchMs]  watchdog period (authority freshness, foreground pressure); 0 disables the timer
 * @param {number} [o.maxCacheBytes]
 */
export function createRelayHelper(o) {
  const locks = o.locks === undefined ? globalThis.navigator?.locks : o.locks;
  const doFetch = o.fetch || ((...a) => globalThis.fetch(...a));
  const now = o.now || (() => Date.now());
  const sleep = o.sleep || abortableWait;
  const delay = o.delay || abortableWait;
  const watchMs = o.watchMs ?? RELAY_LIMITS.watchMs;
  const maxCache = Math.min(o.maxCacheBytes ?? RELAY_LIMITS.maxCacheBytes, RELAY_LIMITS.maxCacheBytes);
  const cache = new Map(); // leaseId -> { lease, bytes, swarm, until }
  const release = new Set();
  const counters = { peerPieces: 0, originPieces: 0, rejected: 0 };
  let state = locks && typeof locks.request === 'function' ? 'idle' : 'unsupported';
  let run = null; // { target, ctrl, ended, fill, authorityUntil, epoch, pendingBytes, tell }
  let generation = 0; // bumps on every start/stop/destroy: only the latest lifecycle request may take effect
  let transitions = Promise.resolve(); // start/stop are serialized

  const usedBytes = () => { let n = 0; for (const e of cache.values()) n += e.bytes.length; return n; };
  const sessionOf = (target) => { try { return typeof target.session === 'function' ? target.session() : target.session; } catch { return null; } };
  const busy = (r) => { try { return !!r.target.isBusy?.(); } catch { return true; } };
  const fresh = (r) => now() <= r.authorityUntil;

  function drop(leaseId, { tell = true } = {}) {
    const e = cache.get(leaseId);
    if (!e) return;
    cache.delete(leaseId);
    try { e.swarm?.leave(); } catch { /* gone */ }
    if (tell) release.add(leaseId);
  }
  function dropAll({ tell = true } = {}) { for (const id of [...cache.keys()]) drop(id, { tell }); }

  // Runs on every transition and on the watchdog: stale authority, foreground pressure and expired entries end
  // auxiliary work at once. Returns whether the run itself is still alive (the loop then reports again).
  function check(r) {
    if (run !== r || r.ctrl.signal.aborted) return false;
    const time = now();
    // A renewal replaced the session that admitted this authority: nothing it admitted survives.
    if (r.sessionId !== null && sessionOf(r.target)?.sessionId !== r.sessionId) {
      r.sessionId = null; r.authorityUntil = 0;
      if (cache.size) dropAll();
      r.fill?.ctrl.abort();
    }
    if (!fresh(r)) {
      if (cache.size) { dropAll(); state = 'stale'; }
      r.fill?.ctrl.abort();
    }
    for (const [id, e] of cache) if (e.until < time) drop(id);
    if (r.fill && busy(r)) r.fill.ctrl.abort();
    return true;
  }
  // A fill may only move forward while its run lives, its authority is fresh, its session is unchanged and it was
  // not cancelled.
  const canFill = (r, child, s) => check(r) && fresh(r) && !child.signal.aborted && sessionOf(r.target)?.sessionId === s.sessionId;

  function reportBody(r) {
    const want = [...new Set((r.target.getWant?.() || []).filter((i) => Number.isSafeInteger(i) && i >= 0))].slice(0, RELAY_LIMITS.maxWant);
    const inventory = [...cache.values()].slice(0, RELAY_LIMITS.maxInventory).map((e) => ({ leaseId: e.lease.leaseId, index: e.lease.index }));
    return { sharingEnabled: true, foregroundBusy: busy(r), cacheUsedBytes: usedBytes(), cacheReservedBytes: r.pendingBytes, want, inventory, release: [...release].slice(0, RELAY_LIMITS.maxRelease) };
  }

  async function call(r, fn, timeoutMs = RELAY_LIMITS.requestTimeoutMs) {
    const c = childSignal(r.fill?.signal || r.ctrl.signal, timeoutMs);
    try { return await fn(c.signal); } finally { c.done(); }
  }

  async function confirm(r, child, s, lease) {
    for (let attempt = 0; attempt < RELAY_LIMITS.confirmAttempts; attempt++) {
      if (!canFill(r, child, s) || now() >= lease.expiresAt) return false;
      try {
        await call(r, (signal) => o.api.relayComplete(s, lease.leaseId, lease.sha256, signal), lease.expiresAt - now());
        return true;
      } catch (e) {
        // The node settles asynchronously; a conflict before that settlement is retried within the lease only.
        const wait = Math.min(2_000, 250 * 2 ** attempt);
        if (e?.status !== 409 || now() + wait >= lease.expiresAt) return false;
        await delay(wait, child.signal);
      }
    }
    return false;
  }

  async function fromOrigin(r, child, s, lease) {
    let grant;
    try { grant = await call(r, (signal) => o.api.relayOrigin(s, lease.leaseId, signal)); } catch { return null; }
    const url = grant && checkOriginUrl(grant.url, lease);
    if (!url || !canFill(r, child, s)) return null;
    // The origin grant's expiresAt is Unix SECONDS (the capability's own `e`), unlike lease expiresAt in ms.
    const grantSeconds = Number(grant.expiresAt);
    const until = Math.min(lease.expiresAt, Number.isSafeInteger(grantSeconds) && grantSeconds > 0 ? grantSeconds * 1000 : lease.expiresAt);
    try {
      return await call(r, async (signal) => {
        const res = await doFetch(url, { cache: 'no-store', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer', signal });
        return res.ok ? readExact(res, lease.length, signal) : null;
      }, until - now());
    } catch { return null; }
  }

  async function fill(r, lease) {
    if (usedBytes() + r.pendingBytes + lease.length > maxCache || busy(r) || !fresh(r)) return;
    const s = sessionOf(r.target);
    if (!s) return;
    const child = childSignal(r.ctrl.signal, lease.expiresAt - now());
    r.fill = child;
    r.pendingBytes += lease.length;
    let swarm = null, bytes = null, source = 'origin';
    try {
      if (o.joinPartial && lease.hasMetadata) {
        try {
          const torrent = await call(r, (signal) => o.api.relayLeaseTorrent(s, lease.leaseId, signal));
          if (canFill(r, child, s)) swarm = await o.joinPartial({ torrent, lease, signal: child.signal, waitMs: Math.min(RELAY_LIMITS.peerWaitMs, Math.max(0, (lease.expiresAt - now()) / 3)) });
          if (swarm?.piece) { bytes = swarm.piece; source = 'peer'; }
        } catch { /* no swarm: fall back to the one-shot origin fill */ }
      }
      if (!bytes && canFill(r, child, s)) bytes = await fromOrigin(r, child, s, lease);
      if (!bytes || !canFill(r, child, s)) return;
      if (bytes.length !== lease.length || (await digestHex(bytes)) !== lease.sha256) { counters.rejected++; return; }
      if (!(await confirm(r, child, s, lease))) return;
      // Everything is rechecked after the confirmation round trip: a renewal, opt-out or stale authority that
      // happened meanwhile keeps the piece out of the cache and unpublished.
      if (!canFill(r, child, s) || now() >= lease.expiresAt) return;
      cache.set(lease.leaseId, { lease, bytes, swarm, until: Math.min(r.authorityUntil, lease.expiresAt) });
      swarm?.advertise?.(bytes); // only now may a peer learn that this tab holds the piece
      swarm = null; // owned by the cache entry now
      counters[source === 'peer' ? 'peerPieces' : 'originPieces']++;
    } finally {
      r.pendingBytes -= lease.length;
      if (r.fill === child) r.fill = null;
      child.done();
      try { swarm?.leave(); } catch { /* gone */ }
    }
  }

  async function loop(r) {
    let refresh = RELAY_LIMITS.minRefreshMs;
    while (check(r)) {
      try {
        const s = sessionOf(r.target);
        const sentAt = now();
        const body = reportBody(r);
        const rep = await call(r, (signal) => o.api.relayReport(s, body, signal));
        if (!check(r)) return;
        if (sessionOf(r.target)?.sessionId !== s?.sessionId) continue; // answered for a session a renewal replaced
        for (const id of body.release) release.delete(id);
        if (rep?.reason) { state = rep.reason; r.authorityUntil = 0; dropAll({ tell: false }); await sleep(RELAY_LIMITS.maxRefreshMs, r.ctrl.signal); continue; }
        if (r.epoch !== null && rep.epoch !== r.epoch) dropAll();
        r.epoch = rep.epoch;
        r.authorityUntil = sentAt + Math.min(RELAY_LIMITS.maxAuthorityMs, refresh * 2);
        r.sessionId = s.sessionId;
        // The server retained exactly the advertised entries; they live on until the next deadline, never past their lease.
        const kept = new Set(body.inventory.map((e) => e.leaseId));
        for (const [id, e] of cache) if (kept.has(id)) e.until = Math.min(r.authorityUntil, e.lease.expiresAt);
        state = 'active';
        if (!busy(r)) {
          const p = await call(r, (signal) => o.api.relayPlan(s, signal));
          if (!check(r)) return;
          if (r.sessionId !== s.sessionId) continue;
          refresh = Math.min(RELAY_LIMITS.maxRefreshMs, Math.max(RELAY_LIMITS.minRefreshMs, Number(p?.refreshAfterMs) || refresh));
          if (p?.epoch !== r.epoch) { dropAll(); r.epoch = p?.epoch ?? null; }
          for (const raw of Array.isArray(p?.leases) ? p.leases : []) {
            if (!check(r) || busy(r) || !fresh(r)) break;
            const lease = parseLease(raw, now());
            if (lease && !cache.has(lease.leaseId)) await fill(r, lease);
          }
        }
      } catch (e) {
        if (!check(r)) return;
        // Withdrawal or stale authority: the auxiliary bytes go at once; the foreground transfer is unaffected.
        if (e?.status === 401 || e?.status === 403 || e?.status === 404) { r.authorityUntil = 0; dropAll({ tell: false }); state = 'refused'; }
        refresh = Math.min(RELAY_LIMITS.maxRefreshMs, refresh * 2);
      }
      check(r);
      await sleep(refresh, r.ctrl.signal);
    }
  }

  function begin(target) {
    const r = { target, ctrl: new AbortController(), ended: null, fill: null, authorityUntil: 0, sessionId: null, epoch: null, pendingBytes: 0, tell: true, ids: [] };
    run = r;
    state = 'waiting';
    const timer = watchMs > 0 ? setInterval(() => check(r), watchMs) : 0;
    r.ended = Promise.resolve()
      .then(() => locks.request(RELAY_LIMITS.lockName, { ifAvailable: true }, async (lock) => {
        if (!lock) { if (run === r) { state = 'follower'; run = null; } return; } // another tab already relays
        try {
          if (run === r) await loop(r);
        } finally {
          // Every auxiliary byte and swarm of this run goes BEFORE the lock is released to another tab.
          r.ids = [...cache.keys()];
          dropAll({ tell: r.tell });
        }
      }))
      .catch(() => { if (run === r) { state = 'unsupported'; run = null; } })
      .finally(() => clearInterval(timer));
    return r;
  }

  // Cancels the run and waits until its lock callback has cleaned up; returns the leases it held.
  async function end(r, { tell }) {
    r.tell = tell;
    r.ctrl.abort();
    try { await r.ended; } catch { /* already reported */ }
    return r.ids;
  }

  const serial = (fn) => (transitions = transitions.then(fn, fn));

  return {
    /** Starts the helper for one engine's target, or retargets it (cancelling and releasing the previous run). */
    start(target) {
      const gen = ++generation;
      return serial(async () => {
        if (state === 'unsupported' || !target || gen !== generation || run?.target === target) return;
        if (run) { const old = run; run = null; await end(old, { tell: true }); }
        if (gen === generation && !run) begin(target);
      });
    },
    /** Opt-out: cancel now, tell the server, drop every auxiliary byte. Never refunds anything server side. */
    stop() {
      generation++;
      const r = run;
      if (r) r.ctrl.abort(); // cancellation is immediate; the report waits for the cleanup
      return serial(async () => {
        if (!r) return;
        if (run === r) run = null;
        const ids = await end(r, { tell: false });
        release.clear();
        if (state !== 'unsupported') state = 'idle';
        const s = sessionOf(r.target);
        if (s) {
          try { await o.api.relayReport(s, { sharingEnabled: false, release: ids.slice(0, RELAY_LIMITS.maxRelease) }); } catch { /* best effort; server TTL cleans up */ }
        }
      });
    },
    target: () => run?.target ?? null,
    stats: () => ({ state, cachedPieces: cache.size, cacheUsedBytes: usedBytes(), cacheReservedBytes: run?.pendingBytes ?? 0, ...counters }),
    destroy() { generation++; const r = run; run = null; if (r) { r.tell = false; r.ctrl.abort(); } dropAll({ tell: false }); if (state !== 'unsupported') state = 'idle'; },
  };
}
